import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import {
    CoordinateGridOptions,
    CoordinateGridSpec,
    CoordinateQuestionOptions,
    ProblemData,
    ProblemFormatOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { coordinatePrompts as en } from "../Files/Prompts/en/coordinatePlane.ts";
import { coordinatePrompts as es } from "../Files/Prompts/es/coordinatePlane.ts";
import { coordinatePrompts as fr } from "../Files/Prompts/fr/coordinatePlane.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };

/**
 * The coordinate plane (ADR 0085). A grid with lettered points is drawn once
 * beside a group of questions about it; every question reads the grid, and
 * none asks a student to plot anything (ADR 0029 §4).
 *
 * The rules ADR 0085 §3 sets for "exactly one reading" are met by where points
 * are allowed to go, never by drawing at random and checking:
 *
 * - Points sit on crossings of whole-number lines, every one labelled.
 * - No point is on an axis, and on a four-quadrant grid none is one line from
 *   an axis, so a letter never sits on the axis numbers (`coordinateValues`).
 * - No two points are within one line of each other in any direction, so a
 *   letter is always nearest its own point (`isClear`).
 * - Every grid has points that share a coordinate, placed first, so a distance
 *   question always has a pair to ask about (`placePoints`).
 *
 * A Polygons sheet (ADR 0087) draws a shape instead: a rectangle or an
 * L-shape, its corners lettered in order around it (`placePolygon`). Every two
 * of its coordinates differ by at least two or are equal, so the same rules
 * hold without checking.
 *
 * A Missing Corner sheet (ADR 0094) draws no points: the grid is blank, for
 * working, and the question gives three corners of a rectangle as pairs
 * (`buildMissingCornerGroup`).
 *
 * A Distance Between Points sheet (ADR 0125) is the one question built from its
 * answers: the pairs are drawn first and the grid's points follow from them
 * (`placeDistancePoints`), because on a grid this size almost every diagonal
 * distance is irrational and the whole ones need particular legs.
 */

/** Up to this many questions about one grid (ADR 0085 §2). */
export const QUESTIONS_PER_GRID = 4;

/** A triangle's base, height and area (ADR 0092 §4). */
export const TRIANGLE_QUESTIONS = 3;

const NO_BREAK_SPACE = ' ';

/** Three rectangles share one blank grid (ADR 0094 §3). */
export const MISSING_CORNER_QUESTIONS = 3;

/** How many questions one grid carries on this sheet. */
export function questionsPerGrid(question: CoordinateQuestionOptions | undefined): number {
    if (question === CoordinateQuestionOptions.Triangles) return TRIANGLE_QUESTIONS;
    if (question === CoordinateQuestionOptions.MissingCorner) return MISSING_CORNER_QUESTIONS;
    return QUESTIONS_PER_GRID;
}

/** Points on a grid: enough that reading, naming and quadrant questions each get their own. */
const POINT_COUNT = 6;

/** The first six letters, shuffled onto the points, so a grid never skips one. */
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

/** The grid a sheet draws. A Quadrant sheet needs four quadrants, whatever the link said (ADR 0085 §4). */
export function effectiveGrid(settings: Pick<ISettingsProps, 'CoordinateGrid' | 'CoordinateQuestion'>): CoordinateGridOptions {
    if (settings.CoordinateQuestion === CoordinateQuestionOptions.Quadrant) {
        return CoordinateGridOptions.FourQuadrants;
    }
    return settings.CoordinateGrid ?? CoordinateGridOptions.FirstQuadrant;
}

/** The axes' range: 0 to 10, or -6 to 6. */
export function gridRange(grid: CoordinateGridOptions): { min: number; max: number } {
    return grid === CoordinateGridOptions.FourQuadrants ? { min: -6, max: 6 } : { min: 0, max: 10 };
}

/**
 * The values a point's coordinate may take. First quadrant: 1 to 9, off both
 * axes and inside the outer lines, so a letter to the point's upper right
 * stays on the grid. Four quadrants: 2 to 5 either side of zero, so a point is
 * never on an axis or next to one, and never on the outer line.
 */
export function coordinateValues(grid: CoordinateGridOptions): number[] {
    if (grid === CoordinateGridOptions.FourQuadrants) {
        return [-5, -4, -3, -2, 2, 3, 4, 5];
    }
    return [1, 2, 3, 4, 5, 6, 7, 8, 9];
}

type Point = { x: number; y: number };

/** At least two lines from every placed point, in both directions at once. */
function isClear(candidate: Point, placed: Point[]): boolean {
    return placed.every(
        (point) => Math.max(Math.abs(point.x - candidate.x), Math.abs(point.y - candidate.y)) >= 2
    );
}

/** Two values from the list at least two apart, in random order. */
function twoApart(values: number[]): [number, number] {
    const a = values[getRandomInt(0, values.length - 1)];
    const far = values.filter((value) => Math.abs(value - a) >= 2);
    return [a, far[getRandomInt(0, far.length - 1)]];
}

/**
 * The points of one grid. A sheet that can be pinned to distance questions
 * gets a rectangle's four corners, which is four pairs that share a
 * coordinate; any other sheet gets an L of three points, which is two. The
 * rest are placed anywhere clear of every point already down.
 */
export function placePoints(grid: CoordinateGridOptions, distanceOnly: boolean): Point[] {
    const values = coordinateValues(grid);
    const [x1, x2] = twoApart(values);
    let [y1, y2] = twoApart(values);
    // A square's four sides are one answer four times; keep the sides unequal.
    while (Math.abs(y1 - y2) === Math.abs(x1 - x2)) {
        [y1, y2] = twoApart(values);
    }
    const points: Point[] = distanceOnly
        ? [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }]
        : [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }];

    const candidates = shuffle(values.flatMap((x) => values.map((y) => ({ x, y }))));
    for (const candidate of candidates) {
        if (points.length >= POINT_COUNT) break;
        if (isClear(candidate, points)) points.push(candidate);
    }
    return points;
}

/** The shapes a Polygons grid draws (ADR 0087 §2). */
export type PolygonShape = 'rectangle' | 'lShape';

/** Three values from the list, each at least two from the next, in increasing order. */
function threeApart(values: number[]): [number, number, number] {
    const triples: [number, number, number][] = [];
    for (const a of values) {
        for (const b of values) {
            for (const c of values) {
                if (b - a >= 2 && c - b >= 2) triples.push([a, b, c]);
            }
        }
    }
    return triples[getRandomInt(0, triples.length - 1)];
}

/**
 * A shape's corners, anticlockwise, starting at a random corner. A rectangle's
 * sides are unequal, for the reason `placePoints` gives. An L-shape is the
 * rectangle from x1 to x3 and y1 to y3 with one corner rectangle cut away,
 * which corner at random.
 */
export function placePolygon(grid: CoordinateGridOptions, shape: PolygonShape): Point[] {
    const values = coordinateValues(grid);
    let corners: Point[];
    if (shape === 'rectangle') {
        const [x1, x2] = twoApart(values).sort((a, b) => a - b);
        let [y1, y2] = twoApart(values).sort((a, b) => a - b);
        while (y2 - y1 === x2 - x1) {
            [y1, y2] = twoApart(values).sort((a, b) => a - b);
        }
        corners = [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }];
    } else {
        const [x1, x2, x3] = threeApart(values);
        const [y1, y2, y3] = threeApart(values);
        const cutAway: Point[][] = [
            // Top right, top left, bottom right, bottom left.
            [{ x: x1, y: y1 }, { x: x3, y: y1 }, { x: x3, y: y2 }, { x: x2, y: y2 }, { x: x2, y: y3 }, { x: x1, y: y3 }],
            [{ x: x1, y: y1 }, { x: x3, y: y1 }, { x: x3, y: y3 }, { x: x2, y: y3 }, { x: x2, y: y2 }, { x: x1, y: y2 }],
            [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x3, y: y2 }, { x: x3, y: y3 }, { x: x1, y: y3 }],
            [{ x: x2, y: y1 }, { x: x3, y: y1 }, { x: x3, y: y3 }, { x: x1, y: y3 }, { x: x1, y: y2 }, { x: x2, y: y2 }],
        ];
        corners = cutAway[getRandomInt(0, cutAway.length - 1)];
    }
    const start = getRandomInt(0, corners.length - 1);
    return [...corners.slice(start), ...corners.slice(0, start)];
}

/** A rectangle's four corners in order around it, as `placeRectangleCorners` returns them. */
export type RectangleCorners = [Point, Point, Point, Point];

/**
 * A rectangle for a Missing Corner question (ADR 0094 §4): corners from
 * `coordinateValues`, sides at least two long and unequal, named A to D in
 * order around it from a random corner in a random direction. With
 * `crossing`, the rectangle straddles an axis (only a four-quadrant grid has
 * values on both sides of one).
 */
export function placeRectangleCorners(grid: CoordinateGridOptions, crossing: boolean): RectangleCorners {
    const values = coordinateValues(grid);
    for (let tries = 0; tries < 10000; tries++) {
        const [x1, x2] = twoApart(values).sort((a, b) => a - b);
        const [y1, y2] = twoApart(values).sort((a, b) => a - b);
        if (y2 - y1 === x2 - x1) continue;
        if (crossing && !(x1 < 0 && x2 > 0) && !(y1 < 0 && y2 > 0)) continue;
        const around = [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }];
        const start = getRandomInt(0, 3);
        const turned = [...around.slice(start), ...around.slice(0, start)];
        const corners = getRandomInt(0, 1) === 0 ? turned : [turned[0], turned[3], turned[2], turned[1]];
        return corners as RectangleCorners;
    }
    throw new Error(`no ${crossing ? 'crossing ' : ''}rectangle fits a ${grid} grid`);
}

/**
 * Where a triangle's height meets its base's line (ADR 0092 §3): at one end of
 * the base, between the ends, or beyond one end.
 */
export type TriangleKind = 'right' | 'inside' | 'outside';
export const TRIANGLE_KINDS: TriangleKind[] = ['right', 'inside', 'outside'];

/** A triangle with its base on a gridline, and the base's two corners. */
export interface PlacedTriangle {
    corners: Point[];
    base: [Point, Point];
    height: number;
}

/** No angle of a drawn triangle is smaller than this, so none is a sliver (ADR 0092 As built). */
export const MIN_TRIANGLE_ANGLE = 20;

/** A triangle's smallest angle, in degrees. */
export function smallestAngle(corners: Point[]): number {
    return Math.min(
        ...corners.map((corner, i) => {
            const a = corners[(i + 1) % 3];
            const b = corners[(i + 2) % 3];
            const [ax, ay, bx, by] = [a.x - corner.x, a.y - corner.y, b.x - corner.x, b.y - corner.y];
            const cos = (ax * bx + ay * by) / (Math.hypot(ax, ay) * Math.hypot(bx, by));
            return (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI;
        })
    );
}

/**
 * A triangle of the given kind (ADR 0092 §2, §3). Worked in coordinates along
 * the base (u) and across it (v), then turned onto the grid across or up and
 * down. The third corner is two or more lines from the base's line, so it is
 * clear of both base corners, and base × height is even, so the area is whole.
 * No angle is under `MIN_TRIANGLE_ANGLE`.
 */
export function placeTriangle(grid: CoordinateGridOptions, kind: TriangleKind): PlacedTriangle {
    const values = coordinateValues(grid);
    const pick = () => values[getRandomInt(0, values.length - 1)];
    const across = getRandomInt(0, 1) === 0;
    const at = (u: number, v: number): Point => (across ? { x: u, y: v } : { x: v, y: u });

    for (let tries = 0; tries < 10000; tries++) {
        const [u1, u2] = twoApart(values).sort((a, b) => a - b);
        const v0 = pick();
        const u3 = pick();
        const v3 = pick();
        const height = Math.abs(v3 - v0);
        if (height < 2 || ((u2 - u1) * height) % 2 !== 0) continue;
        const foot = u3 === u1 || u3 === u2 ? 'right' : u3 > u1 && u3 < u2 ? 'inside' : 'outside';
        if (foot !== kind) continue;

        let corners = [at(u1, v0), at(u2, v0), at(u3, v3)];
        if (smallestAngle(corners) < MIN_TRIANGLE_ANGLE) continue;
        if (polygonSignedArea(corners) < 0) corners = [corners[1], corners[0], corners[2]];
        const start = getRandomInt(0, 2);
        const base: [Point, Point] = [at(u1, v0), at(u2, v0)];
        return { corners: [...corners.slice(start), ...corners.slice(0, start)], base, height };
    }
    throw new Error(`no ${kind} triangle fits a ${grid} grid`);
}

/** Twice the signed area: positive when the corners run anticlockwise. */
function polygonSignedArea(corners: Point[]): number {
    let twice = 0;
    corners.forEach((a, i) => {
        const b = corners[(i + 1) % corners.length];
        twice += a.x * b.y - b.x * a.y;
    });
    return twice;
}

/** Area by the shoelace formula; every side is on a gridline, so it is a whole number. */
export function polygonArea(corners: Point[]): number {
    return Math.abs(polygonSignedArea(corners)) / 2;
}

export function polygonPerimeter(corners: Point[]): number {
    return corners.reduce((sum, a, i) => {
        const b = corners[(i + 1) % corners.length];
        return sum + Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    }, 0);
}

export function quadrantOf(point: Point): string {
    if (point.x > 0) return point.y > 0 ? 'I' : 'IV';
    return point.y > 0 ? 'II' : 'III';
}

/** Whether a diagonal offset is a whole distance on these grids: legs 3 and 4, or 6 and 8 (ADR 0125 §2). */
export function isWholeOffset(dx: number, dy: number): boolean {
    const legs = [Math.abs(dx), Math.abs(dy)].sort((a, b) => a - b);
    return (legs[0] === 3 && legs[1] === 4) || (legs[0] === 6 && legs[1] === 8);
}

/**
 * A distance the generator will not ask to the nearest tenth: one whose tenths
 * come within 0.01 of a half, where two calculators could disagree on the
 * rounding (ADR 0125 §2). Of all legs up to 10 only 1 and 10 (sum 101) is one.
 */
export function isRefusedDistance(dx: number, dy: number): boolean {
    const tenths = 10 * Math.sqrt(dx * dx + dy * dy);
    return Math.abs(tenths - Math.floor(tenths) - 0.5) < 0.01;
}

/** Whether a diagonal offset suits a rounded answer: both legs at least 1, the distance not whole, and not refused. */
export function isRoundedOffset(dx: number, dy: number): boolean {
    if (dx === 0 || dy === 0) return false;
    const sum = dx * dx + dy * dy;
    return !Number.isInteger(Math.sqrt(sum)) && !isRefusedDistance(dx, dy);
}

/** A distance's tenths, as a whole number: 36 for 3.6. */
export function distanceInTenths(sum: number): number {
    return Math.round(10 * Math.sqrt(sum));
}

/** A Distance Between Points grid: its six points, and the pairs to ask about. */
export interface DistancePlacement {
    points: Point[];
    whole: [Point, Point][];
    rounded: [Point, Point][];
}

/** Tries before `placeDistancePoints` falls back to a fixed layout (ADR 0125 §4, as ADR 0121 does for area figures). */
const DISTANCE_ATTEMPTS = 200;

const samePoint = (a: Point, b: Point) => a.x === b.x && a.y === b.y;

/** Whether pair p-q is the pair a-b, either way round. */
const isPair = (p: Point, q: Point, a: Point, b: Point) =>
    (samePoint(p, a) && samePoint(q, b)) || (samePoint(p, b) && samePoint(q, a));

/** Every offset either grid can hold has legs up to 10; sorted into the kinds once, since the search asks thousands of times. */
const offsetKey = (dx: number, dy: number) => (dx + 10) * 21 + (dy + 10);
const WHOLE_OFFSETS = new Set<number>();
const ROUNDED_OFFSETS = new Set<number>();
for (let dx = -10; dx <= 10; dx++) {
    for (let dy = -10; dy <= 10; dy++) {
        if (isWholeOffset(dx, dy)) WHOLE_OFFSETS.add(offsetKey(dx, dy));
        if (isRoundedOffset(dx, dy)) ROUNDED_OFFSETS.add(offsetKey(dx, dy));
    }
}

/**
 * One try at a placement: whole pairs first (the harder to fit), then rounded
 * ones. Each pair reuses a point already down when it can, which is how four
 * pairs fit six points. Every point added is clear of all the others, so
 * ADR 0085 §3 holds without checking. Null when a pair finds no place.
 */
function drawDistancePlacement(values: number[], nWhole: number, nRounded: number): DistancePlacement | null {
    const spots: Point[] = values.flatMap((x) => values.map((y) => ({ x, y })));
    const points: Point[] = [];
    const placed: { whole: [Point, Point][]; rounded: [Point, Point][] } = { whole: [], rounded: [] };
    const named = (a: Point, b: Point) =>
        placed.whole.some(([p, q]) => isPair(p, q, a, b)) || placed.rounded.some(([p, q]) => isPair(p, q, a, b));
    const down = (spot: Point) => points.some((point) => samePoint(point, spot));

    const kinds: ('whole' | 'rounded')[] = [
        ...Array.from({ length: nWhole }, () => 'whole' as const),
        ...Array.from({ length: nRounded }, () => 'rounded' as const),
    ];
    for (const kind of kinds) {
        const suits = kind === 'whole' ? WHOLE_OFFSETS : ROUNDED_OFFSETS;
        const fits = (a: Point, b: Point) => suits.has(offsetKey(b.x - a.x, b.y - a.y)) && !named(a, b);

        // A pair can put down no new point, one, or two. `pairWith(n)` is a random pair that
        // puts down n, or null; the search is by sampling, so it stays quick.
        const pairWith = (added: number): [Point, Point] | null => {
            if (points.length + added > POINT_COUNT) return null;
            if (added === 2) {
                for (const a of shuffle(spots)) {
                    if (!isClear(a, points)) continue;
                    const ends = spots.filter((b) => fits(a, b) && isClear(b, points) && isClear(b, [a]));
                    if (ends.length > 0) return [a, ends[getRandomInt(0, ends.length - 1)]];
                }
                return null;
            }
            // Starting from a point already down, ending on one (0) or on a clear spot (1).
            const pairs: [Point, Point][] = [];
            for (const a of points) {
                for (const b of spots) {
                    if (samePoint(a, b) || !fits(a, b)) continue;
                    if (added === 0 ? down(b) : !down(b) && isClear(b, points)) pairs.push([a, b]);
                }
            }
            return pairs.length > 0 ? pairs[getRandomInt(0, pairs.length - 1)] : null;
        };

        // Sharing a point keeps the grid readable and leaves room, so it is favoured.
        const weights = [2, 5, 3];
        let remaining = [0, 1, 2];
        let pair: [Point, Point] | null = null;
        while (!pair && remaining.length > 0) {
            let roll = getRandomInt(1, remaining.reduce((sum, added) => sum + weights[added], 0));
            const added = remaining.find((candidate) => (roll -= weights[candidate]) <= 0)!;
            pair = pairWith(added);
            remaining = remaining.filter((candidate) => candidate !== added);
        }
        if (!pair) return null;
        for (const spot of pair) if (!down(spot)) points.push(spot);
        placed[kind].push(pair);
    }

    // The spare points go anywhere clear, so a pair is found by reading, not by elimination.
    for (const spot of shuffle(spots)) {
        if (points.length >= POINT_COUNT) break;
        if (isClear(spot, points)) points.push(spot);
    }
    return points.length === POINT_COUNT ? { points, ...placed } : null;
}

/**
 * A layout that satisfies every rule, for the rare draw that finds none in
 * `DISTANCE_ATTEMPTS` tries. Two whole pairs and two rounded, as indices into
 * the six points; a sheet that wants fewer takes the first of each.
 */
const DISTANCE_FALLBACK: Record<CoordinateGridOptions, { points: Point[]; whole: [number, number][]; rounded: [number, number][] }> = {
    [CoordinateGridOptions.FirstQuadrant]: {
        points: [{ x: 1, y: 1 }, { x: 4, y: 5 }, { x: 7, y: 9 }, { x: 8, y: 6 }, { x: 2, y: 8 }, { x: 9, y: 3 }],
        whole: [[0, 1], [0, 2]],
        rounded: [[1, 3], [2, 4]],
    },
    [CoordinateGridOptions.FourQuadrants]: {
        points: [{ x: 2, y: 2 }, { x: 5, y: -2 }, { x: -4, y: -3 }, { x: 2, y: 5 }, { x: -5, y: 3 }, { x: 3, y: -5 }],
        whole: [[0, 1], [2, 3]],
        rounded: [[1, 5], [2, 4]],
    },
};

export function distanceFallback(grid: CoordinateGridOptions, nWhole: number, nRounded: number): DistancePlacement {
    const { points, whole, rounded } = DISTANCE_FALLBACK[grid];
    const pairOf = ([i, j]: [number, number]): [Point, Point] => [points[i], points[j]];
    return { points, whole: whole.slice(0, nWhole).map(pairOf), rounded: rounded.slice(0, nRounded).map(pairOf) };
}

/**
 * The six points of a Distance Between Points grid and the pairs its questions
 * name (ADR 0125 §4): `nWhole` pairs with a whole distance, `nRounded` with a
 * rounded one, every pair diagonal and none named twice. All of ADR 0085 §3
 * holds except its last rule (two points sharing a coordinate), which this
 * question does not want.
 */
export function placeDistancePoints(grid: CoordinateGridOptions, nWhole: number, nRounded: number): DistancePlacement {
    const values = coordinateValues(grid);
    for (let attempt = 0; attempt < DISTANCE_ATTEMPTS; attempt++) {
        const placement = drawDistancePlacement(values, nWhole, nRounded);
        if (placement) return placement;
    }
    return distanceFallback(grid, nWhole, nRounded);
}

/** The question types a sheet draws from, in rotation order. */
export function coordinateQuestionTypes(
    question: CoordinateQuestionOptions,
    grid: CoordinateGridOptions
): CoordinateQuestionOptions[] {
    if (question !== CoordinateQuestionOptions.Mixed) {
        return [question];
    }
    const types = [
        CoordinateQuestionOptions.ReadPoint,
        CoordinateQuestionOptions.NamePoint,
        CoordinateQuestionOptions.Distance,
    ];
    // On the first quadrant every point is in quadrant I: nothing to ask.
    return grid === CoordinateGridOptions.FourQuadrants ? [...types, CoordinateQuestionOptions.Quadrant] : types;
}

/** What a group has asked about its grid (ADR 0085 §2). */
interface GroupUsage {
    /** A point read, named or placed in a quadrant is not used again on that grid. */
    points: Set<string>;
    pairs: Set<string>;
}

function tryBuildQuestion(
    type: CoordinateQuestionOptions,
    spec: CoordinateGridSpec,
    used: GroupUsage,
    language: PrintedLanguage
): { prompt: string; answer: string; commit: () => void } | null {
    const t = PROMPTS[language];
    const fresh = spec.points.filter((point) => !used.points.has(point.label));

    if (type === CoordinateQuestionOptions.Distance) {
        const pairs: [typeof spec.points[0], typeof spec.points[0]][] = [];
        for (const a of spec.points) {
            for (const b of spec.points) {
                const aligned = a.x === b.x || a.y === b.y;
                if (a.label < b.label && aligned && !used.pairs.has(a.label + b.label)) pairs.push([a, b]);
            }
        }
        if (pairs.length === 0) return null;
        const [a, b] = shuffle(pairs[getRandomInt(0, pairs.length - 1)]);
        const distance = Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
        const key = [a.label, b.label].sort().join('');
        return {
            prompt: t.distance(a.label, b.label),
            answer: t.units(distance),
            commit: () => used.pairs.add(key),
        };
    }

    if (fresh.length === 0) return null;
    const point = fresh[getRandomInt(0, fresh.length - 1)];
    const commit = () => used.points.add(point.label);
    switch (type) {
        case CoordinateQuestionOptions.NamePoint:
            return { prompt: t.namePoint(t.pair(point.x, point.y)), answer: point.label, commit };
        case CoordinateQuestionOptions.Quadrant:
            return { prompt: t.quadrant(point.label), answer: quadrantOf(point), commit };
        default:
            return { prompt: t.readPoint(point.label), answer: t.pair(point.x, point.y), commit };
    }
}

/**
 * One shape and up to `want` questions about it (ADR 0087 §3): its area, its
 * perimeter, then one horizontal side and one vertical side, in that order, so
 * a two-question grid gets area and perimeter.
 */
function buildPolygonGroup(
    grid: CoordinateGridOptions,
    shape: PolygonShape,
    want: number,
    groupKey: string,
    startKey: number,
    language: PrintedLanguage
): ProblemData[] {
    const t = PROMPTS[language];
    const points = placePolygon(grid, shape).map((point, i) => ({ label: LETTERS[i], ...point }));
    const spec: CoordinateGridSpec = { ...gridRange(grid), points, outline: true };
    const name = t.shapeName(shape === 'rectangle', points.map((point) => point.label).join(''));

    // One side across and one up and down, of different lengths, so the two
    // answers differ (an L's two inner sides can be equal). There is always
    // such a pair: the longer of the shape's width and height is longer than
    // the short sides running the other way.
    const sides = points.map((a, i) => [a, points[(i + 1) % points.length]]);
    const length = ([a, b]: typeof points) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
    const pairs = sides
        .filter(([a, b]) => a.y === b.y)
        .flatMap((across) => sides.filter(([a, b]) => a.x === b.x).map((upAndDown) => [across, upAndDown]))
        .filter(([across, upAndDown]) => length(across) !== length(upAndDown));
    const [across, upAndDown] = pairs[getRandomInt(0, pairs.length - 1)];
    const sideQuestion = ([a, b]: typeof points) => ({
        prompt: t.side([a.label, b.label].sort().join('')),
        answer: t.units(length([a, b])),
    });

    const questions = [
        { prompt: t.area(name), answer: t.squareUnits(polygonArea(points)) },
        { prompt: t.perimeter(name), answer: t.units(polygonPerimeter(points)) },
        ...shuffle([sideQuestion(across), sideQuestion(upAndDown)]),
    ];

    return questions.slice(0, want).map((question, i) => ({
        key: startKey + i,
        numbers: [],
        operator: "",
        display: [question.prompt, question.answer],
        coordinateQuestion: CoordinateQuestionOptions.Polygons,
        problemGroup: { key: groupKey, format: ProblemFormatOptions.CoordinatePlane, coordinateGrid: spec },
    }));
}

/**
 * One triangle and its three questions (ADR 0092 §4): the base, the height to
 * it, then the area, in the order the formula uses them.
 */
function buildTriangleGroup(
    grid: CoordinateGridOptions,
    kind: TriangleKind,
    want: number,
    groupKey: string,
    startKey: number,
    language: PrintedLanguage
): ProblemData[] {
    const t = PROMPTS[language];
    const triangle = placeTriangle(grid, kind);
    const points = triangle.corners.map((point, i) => ({ label: LETTERS[i], ...point }));
    const spec: CoordinateGridSpec = { ...gridRange(grid), points, outline: true };
    const labelOf = (corner: Point) => points.find((p) => p.x === corner.x && p.y === corner.y)!.label;
    const baseName = triangle.base.map(labelOf).sort().join('');
    const baseLength = Math.abs(triangle.base[0].x - triangle.base[1].x) + Math.abs(triangle.base[0].y - triangle.base[1].y);
    const name = t.triangleName(points.map((point) => point.label).join(''));

    const questions = [
        { prompt: t.base(baseName), answer: t.units(baseLength) },
        { prompt: t.height(baseName), answer: t.units(triangle.height) },
        { prompt: t.area(name), answer: t.squareUnits((baseLength * triangle.height) / 2) },
    ];

    return questions.slice(0, want).map((question, i) => ({
        key: startKey + i,
        numbers: [],
        operator: "",
        display: [question.prompt, question.answer],
        coordinateQuestion: CoordinateQuestionOptions.Triangles,
        problemGroup: { key: groupKey, format: ProblemFormatOptions.CoordinatePlane, coordinateGrid: spec },
    }));
}

/**
 * One blank grid and up to `want` rectangles to ask about (ADR 0094). Each
 * question gives three corners, in alphabetical order, and asks the fourth;
 * the missing letters of one grid's questions are all different. On four
 * quadrants at least one rectangle crosses an axis. `seen` holds the page's
 * rectangles so none repeats.
 */
function buildMissingCornerGroup(
    grid: CoordinateGridOptions,
    want: number,
    groupKey: string,
    startKey: number,
    language: PrintedLanguage,
    seen: Set<string>
): ProblemData[] {
    const t = PROMPTS[language];
    const spec: CoordinateGridSpec = { ...gridRange(grid), points: [] };
    const missingLetters = shuffle(['A', 'B', 'C', 'D']);
    const fourQuadrants = grid === CoordinateGridOptions.FourQuadrants;
    const crosses = (corners: RectangleCorners) =>
        corners[0].x * corners[2].x < 0 || corners[0].y * corners[2].y < 0;
    let anyCrossing = false;

    const questions: { prompt: string; answer: string }[] = [];
    for (let i = 0; i < want; i++) {
        // The last rectangle is drawn to cross if none before it did.
        const mustCross = fourQuadrants && i === want - 1 && !anyCrossing;
        let corners: RectangleCorners;
        let key: string;
        do {
            corners = placeRectangleCorners(grid, mustCross);
            key = corners.map((corner) => `${corner.x},${corner.y}`).sort().join(';');
        } while (seen.has(key));
        seen.add(key);
        anyCrossing = anyCrossing || crosses(corners);

        const missing = missingLetters[i % missingLetters.length];
        const given = ['A', 'B', 'C', 'D']
            .filter((letter) => letter !== missing)
            .map((letter) => {
                const corner = corners['ABCD'.indexOf(letter)];
                // A corner's letter and pair never split across a line: the first
                // rendered sheets broke "B (-2," from "5)".
                return `${letter} ${t.pair(corner.x, corner.y)}`.replace(/ /g, NO_BREAK_SPACE);
            });
        const answer = corners['ABCD'.indexOf(missing)];
        questions.push({ prompt: t.missingCorner(given, missing), answer: t.pair(answer.x, answer.y) });
    }

    return questions.map((question, i) => ({
        key: startKey + i,
        numbers: [],
        operator: "",
        display: [question.prompt, question.answer],
        coordinateQuestion: CoordinateQuestionOptions.MissingCorner,
        problemGroup: { key: groupKey, format: ProblemFormatOptions.CoordinatePlane, coordinateGrid: spec },
    }));
}

/**
 * One grid and up to `want` Distance Between Points questions (ADR 0125 §2):
 * half with a whole answer and half rounded, the extra one going to the rounded
 * when `want` is odd, dealt in a random order. Each names two points that share
 * no coordinate; the key gives the legs squared, their sum, then the distance.
 */
function buildPointDistanceGroup(
    grid: CoordinateGridOptions,
    want: number,
    groupKey: string,
    startKey: number,
    language: PrintedLanguage
): ProblemData[] {
    const t = PROMPTS[language];
    const nWhole = Math.floor(want / 2);
    const placement = placeDistancePoints(grid, nWhole, want - nWhole);
    const letters = shuffle(LETTERS);
    const points = placement.points
        .map((point, i) => ({ label: letters[i], ...point }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const spec: CoordinateGridSpec = { ...gridRange(grid), points };
    const labelOf = (point: Point) => points.find((p) => samePoint(p, point))!.label;

    const asked = [
        ...placement.whole.map((pair) => ({ pair, whole: true })),
        ...placement.rounded.map((pair) => ({ pair, whole: false })),
    ];
    const questions = shuffle(asked).map(({ pair, whole }) => {
        const [from, to] = shuffle(pair);
        // The legs are lengths along the grid, horizontal first (ADR 0125 §3).
        const horizontal = Math.abs(from.x - to.x);
        const vertical = Math.abs(from.y - to.y);
        const sum = horizontal * horizontal + vertical * vertical;
        return {
            prompt: t.pointDistance(labelOf(from), labelOf(to)),
            answer: whole
                ? t.pointDistanceWhole(horizontal, vertical, sum, Math.sqrt(sum))
                : t.pointDistanceRounded(horizontal, vertical, sum, distanceInTenths(sum)),
        };
    });

    return questions.map((question, i) => ({
        key: startKey + i,
        numbers: [],
        operator: "",
        display: [question.prompt, question.answer],
        coordinateQuestion: CoordinateQuestionOptions.PointDistance,
        problemGroup: { key: groupKey, format: ProblemFormatOptions.CoordinatePlane, coordinateGrid: spec },
    }));
}

/**
 * One grid and up to `want` questions about it, all tagged with the same group
 * so the page draws the grid once beside them. `startTypeIndex` carries a
 * Mixed sheet's rotation across grids, for the reason `buildBarGraphGroup`
 * gives.
 */
export function buildCoordinateGroup(
    settings: Pick<ISettingsProps, 'CoordinateQuestion' | 'CoordinateGrid'>,
    want: number,
    groupKey: string,
    startKey: number,
    startTypeIndex: number,
    language: PrintedLanguage,
    shape: PolygonShape = 'rectangle',
    triangleKind: TriangleKind = 'right',
    seenRectangles: Set<string> = new Set()
): { problems: ProblemData[]; nextKey: number; nextTypeIndex: number } {
    const grid = effectiveGrid(settings);
    const question = settings.CoordinateQuestion ?? CoordinateQuestionOptions.Mixed;
    if (question === CoordinateQuestionOptions.Polygons) {
        const problems = buildPolygonGroup(grid, shape, want, groupKey, startKey, language);
        return { problems, nextKey: startKey + problems.length, nextTypeIndex: 0 };
    }
    if (question === CoordinateQuestionOptions.Triangles) {
        const problems = buildTriangleGroup(grid, triangleKind, want, groupKey, startKey, language);
        return { problems, nextKey: startKey + problems.length, nextTypeIndex: 0 };
    }
    if (question === CoordinateQuestionOptions.MissingCorner) {
        const problems = buildMissingCornerGroup(grid, want, groupKey, startKey, language, seenRectangles);
        return { problems, nextKey: startKey + problems.length, nextTypeIndex: 0 };
    }
    if (question === CoordinateQuestionOptions.PointDistance) {
        const problems = buildPointDistanceGroup(grid, want, groupKey, startKey, language);
        return { problems, nextKey: startKey + problems.length, nextTypeIndex: 0 };
    }
    const types = coordinateQuestionTypes(question, grid);
    const letters = shuffle(LETTERS);
    const points = placePoints(grid, question === CoordinateQuestionOptions.Distance)
        .map((point, i) => ({ label: letters[i], ...point }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const spec: CoordinateGridSpec = { ...gridRange(grid), points };

    const used: GroupUsage = { points: new Set(), pairs: new Set() };
    const problems: ProblemData[] = [];
    let key = startKey;
    let typeIndex = startTypeIndex;
    let stalled = 0;

    while (problems.length < want && stalled < types.length) {
        const type = types[typeIndex % types.length];
        typeIndex++;
        const picked = tryBuildQuestion(type, spec, used, language);
        if (!picked) {
            stalled++;
            continue;
        }
        stalled = 0;
        picked.commit();
        problems.push({
            key: key++,
            numbers: [],
            operator: "",
            display: [picked.prompt, picked.answer],
            coordinateQuestion: type,
            problemGroup: { key: groupKey, format: ProblemFormatOptions.CoordinatePlane, coordinateGrid: spec },
        });
    }

    return { problems, nextKey: key, nextTypeIndex: typeIndex % types.length };
}

/** A page-shaped set of coordinate-plane problems, up to `questionsPerGrid` to a grid. */
export function generateCoordinatePages(settings: ISettingsProps): ProblemData[][] | null {
    // A Triangles sheet is whole triangles: a count carried over from another
    // question type, such as 4, would leave a grid with only its base asked.
    // A Missing Corner sheet is likewise whole grids of three (ADR 0094 §3).
    const wholeGrids = settings.CoordinateQuestion === CoordinateQuestionOptions.Triangles
        || settings.CoordinateQuestion === CoordinateQuestionOptions.MissingCorner;
    const perGrid = questionsPerGrid(settings.CoordinateQuestion);
    const perPage = wholeGrids
        ? Math.max(perGrid, settings.NumberOfProblemsPerPage - (settings.NumberOfProblemsPerPage % perGrid))
        : settings.NumberOfProblemsPerPage;
    const language = printedLanguageOf(settings);
    const pages: ProblemData[][] = [];
    let nextKey = 1;

    for (let page = 0; page < settings.NumberOfPages; page++) {
        const problems: ProblemData[] = [];
        let groupIndex = 0;
        let typeIndex = 0;
        // A page's shapes alternate, so a two-grid page has one of each (ADR 0087 §2).
        // Drawn only on a Polygons sheet, so other sheets keep their seeds' sheets.
        const firstShape = settings.CoordinateQuestion === CoordinateQuestionOptions.Polygons ? getRandomInt(0, 1) : 0;
        // A page's triangles are of different kinds (ADR 0092 §3), likewise drawn only when asked for.
        const kinds = settings.CoordinateQuestion === CoordinateQuestionOptions.Triangles ? shuffle(TRIANGLE_KINDS) : TRIANGLE_KINDS;
        // A page's rectangles are all different (ADR 0094 §4).
        const seenRectangles = new Set<string>();
        const guardLimit = perPage * 4 + 10;
        let guard = 0;

        while (problems.length < perPage && guard < guardLimit) {
            guard++;
            const want = Math.min(perGrid, perPage - problems.length);
            const group = buildCoordinateGroup(
                settings,
                want,
                `coordinate:${page}:${groupIndex}`,
                nextKey,
                typeIndex,
                language,
                (firstShape + groupIndex) % 2 === 0 ? 'rectangle' : 'lShape',
                kinds[groupIndex % kinds.length],
                seenRectangles
            );
            if (group.problems.length === 0) {
                break;
            }
            nextKey = group.nextKey;
            typeIndex = group.nextTypeIndex;
            problems.push(...group.problems);
            groupIndex++;
        }

        pages.push(problems);
    }

    return pages.some((page) => page.length > 0) ? pages : null;
}
